import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from '../../contexts/AuthContext';
import './LandingPage.css';

interface NavItem {
  path?: string;
  label: string;
  description: string;
  requiredDev?: boolean;
  requiredAdmin?: boolean;
  requiredEmail?: string;
  className?: string;
  /** Visible chip that does not navigate. */
  noop?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: "Fulfillment",
    items: [
      { path: "/order-manager", label: "Orders", description: "Manage and view orders" },
    ]
  },
  {
    title: "Warehousing",
    items: [
      { path: "/scan-label", label: "Scan Label", description: "Scan a warehouse label" },
      { path: "/tasks", label: "Task Queue", description: "Warehouse and fulfillment tasks" },
      { path: "/shelf-indexer", label: "Shelf Indexer", description: "Index shelf locations" },
      { path: "/print-warehouse-labels", label: "Warehouse Labels", description: "Print warehouse labels" },
    ]
  },
  {
    title: "Inventory",
    items: [
      { path: "/search-warehouse", label: "Search Warehouse", description: "Search warehouse inventory" },
      { path: "/bundle-work-orders", label: "Make Bundles", description: "Bundle work orders" },
      { path: "/inventory-counts", label: "Inventory Counts", description: "Cycle count work orders" },
      { path: "/inventory-transfers", label: "Inventory Transfer", description: "Open retail transfer requests" },
    ]
  },
  {
    title: "Supply Chain",
    items: [
      { path: "/receive", label: "Receive", description: "Add newly received inventory to Airtable" },
    ]
  },
  {
    title: "Utilities",
    items: [
      { path: "/admin", label: "Admin Panel", description: "Admin tools", requiredAdmin: true },
      { path: "/dev", label: "Dev Panel", description: "Shopify inventory debug", requiredDev: true },
      { path: "/shelf-audit", label: "Shelf Audit", description: "Audit shelf flags" },
      { path: "/product-weight", label: "Product Weight", description: "Update product weight" },
      { path: "/filter", label: "Filter", description: "Filter orders" },
      { path: "/space-calculator", label: "Space Calculator", description: "Plan box stacking layouts", className: "desktop-only" },
    ]
  },
];

const LandingPage: React.FC = () => {
  const { user, isDev, isAdmin } = useAuth();

  return (
    <div className="landing-container">
      <div className="landing-content">
        {navSections.map((section) => (
          <div key={section.title} className="landing-section">
            <h2 className="landing-section-header">{section.title}</h2>
            <div className="landing-nav-grid">
              {section.items
                .filter((item) => {
                  if (item.requiredDev) return isDev;
                  if (item.requiredAdmin) return isAdmin;
                  if (item.requiredEmail) return user?.email === item.requiredEmail;
                  return true;
                })
                .map((item) => {
                  const className = `landing-nav-card${item.className ? ` ${item.className}` : ''}`;
                  const body = (
                    <>
                      <span className="landing-nav-label">{item.label}</span>
                      <span className="landing-nav-desc">{item.description}</span>
                    </>
                  );
                  if (item.noop || !item.path) {
                    return (
                      <button type="button" key={item.label} className={className}>
                        {body}
                      </button>
                    );
                  }
                  return (
                    <Link key={item.path} to={item.path} className={className}>
                      {body}
                    </Link>
                  );
                })}
            </div>
          </div>
        ))}
      </div>

      {user && (
        <div className="landing-user">
          Logged in as {user.name}
        </div>
      )}
    </div>
  );
};

export default LandingPage;
